// src/components/TutorialOverlay.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Play,
  Lightbulb,
  CheckCircle,
  Menu,
  Target,
} from "lucide-react";
import { getDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const TUTORIAL_STEPS = [
  {
    id: "welcome",
    title: "Welcome to FlowFrame! 🎵",
    content: "Let's take a quick tour of the key features that will help you master complex rhythms and hone your pitch. This will only take a couple minutes!",
    icon: <Lightbulb className="w-6 h-6 text-white" />,
  },
  {
    id: "pattern-workspace",
    title: "The Pattern Overview",
    content: "The main area of the app is your pattern overview. This is where you'll build and edit your rhythmic patterns. Here you can add measures, change time signatures, and even apply dynamics.",
    icon: <Target className="w-5 h-5 text-white" />,
  },
  {
    id: "playback-controls",
    title: "Playback Controls",
    content: "Look for the playback controls above the overview to start and stop your pattern, and set a start measure and count-in. Below the overview, you'll find where to adjust the tempo (BPM) and loop specific sections to focus your practice.",
    icon: <Play className="w-5 h-5 text-white" />,
  },
  {
    id: "measure-details",
    title: "Advanced Features",
    content: "Click '>>' in any measure or head to 'Measure detail editor' below to take you to advanced features like subdivisions for complex rhythms, drones for harmonic practice, and tuplets for creating polyrhythms.",
    icon: <CheckCircle className="w-5 h-5 text-white" />,
  },
  {
    id: "menu",
    title: "Menu & More",
    content: "Find the menu icon to access powerful features like saving your patterns to the library, exporting to MIDI, recalibrating your tuning, enabling other advanced features, and managing your account.",
    icon: <Menu className="w-5 h-5 text-white" />,
  },
  {
    id: "finish",
    title: "You're Ready! 🚀",
    content: "That's the tour! Start by creating your first pattern, or explore the Library, Practice Tracker, and Blog tabs to see what else FlowFrame can do. Follow @flowframeapp on Instagram for updates and tips!",
    icon: <CheckCircle className="w-6 h-6 text-white" />,
  },
];


const TutorialCard = ({ step, currentStep, totalSteps, onNext, onPrev, onSkip, isVisible }) => {
  const cardStyle = {
    position: 'fixed',
    zIndex: 10000,
    width: '90%',
    maxWidth: '400px',
    top: '50%',
    left: '50%',
    transform: isVisible ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.95)',
    transition: 'opacity 0.3s, transform 0.3s',
    opacity: isVisible ? 1 : 0,
    pointerEvents: isVisible ? 'auto' : 'none',
  };
  
  if (!step) return null;

  const progress = ((currentStep + 1) / totalSteps) * 100;
  
  return (
    <div style={cardStyle} className="bg-gray-800 text-white rounded-xl shadow-2xl border border-gray-700 flex flex-col">
      <div className="h-2 bg-gray-700 rounded-t-xl overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="p-6 flex-grow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gray-700 rounded-full border border-gray-600">
              {step.icon}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{step.title}</h3>
              <div className="text-sm text-gray-400">Step {currentStep + 1} of {totalSteps}</div>
            </div>
          </div>
          <button onClick={onSkip} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-700 transition-colors" aria-label="Close tutorial">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-300 leading-relaxed mb-6">{step.content}</p>
      </div>

      <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-b-xl border-t border-gray-700">
        <button onClick={onSkip} className="text-gray-400 hover:text-white text-sm font-medium transition-colors">
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <button onClick={onPrev} className="flex items-center gap-2 px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all">
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button onClick={onNext} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-blue-500/30">
            {currentStep === totalSteps - 1 ? "Finish" : "Next"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export function TutorialOverlay({ currentUser, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const completeTutorial = useCallback(async () => {
    setIsVisible(false);
    if (currentUser) {
      await updateDoc(doc(db, "users", currentUser.uid), { tutorialCompleted: true });
    } else {
      localStorage.setItem("tutorialCompleted", "true");
    }
    onComplete?.();
  }, [currentUser, onComplete]);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTutorial();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  useEffect(() => {
    const checkStatus = async () => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (!userDoc.exists() || !userDoc.data().tutorialCompleted) {
          setIsVisible(true);
        }
      } else {
        if (localStorage.getItem("tutorialCompleted") !== "true") {
          setIsVisible(true);
        }
      }
    };
    checkStatus();
  }, [currentUser]);

  useEffect(() => {
    if (!isVisible) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') completeTutorial();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, completeTutorial, handleNext, handlePrev]);
  
  const currentTutorialStep = TUTORIAL_STEPS[currentStep];
  const backdropClass = isVisible ? "fixed inset-0 z-[9998] bg-black bg-opacity-60 transition-opacity duration-300" : "fixed inset-0 z-[9998] bg-black bg-opacity-0 pointer-events-none transition-opacity duration-300";

  return (
    <>
      <div className={backdropClass} onClick={completeTutorial} />
      <TutorialCard
        step={currentTutorialStep}
        currentStep={currentStep}
        totalSteps={TUTORIAL_STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={completeTutorial}
        isVisible={isVisible}
      />
    </>
  );
}

export default TutorialOverlay;